import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Phone, Mail, Building2, User, Users } from 'lucide-react';

interface ProspectPipelineProps {
  agentId?: string;
  viewAll?: boolean;
}

const statusSteps = ['new', 'contacted', 'negotiating', 'converted', 'lost'] as const;
const statusColors: Record<string, string> = {
  new: 'bg-blue-100 text-blue-700',
  contacted: 'bg-yellow-100 text-yellow-700',
  negotiating: 'bg-purple-100 text-purple-700',
  converted: 'bg-green-100 text-green-700',
  lost: 'bg-red-100 text-red-700',
};
const typeIcons: Record<string, any> = { agent: User, agency: Users, developer: Building2 };

export function ProspectPipeline({ agentId, viewAll = false }: ProspectPipelineProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ prospect_name: '', prospect_type: 'agent', contact_email: '', contact_phone: '', notes: '' });

  const effectiveAgentId = agentId || user?.id;

  const { data: prospects = [], isLoading } = useQuery({
    queryKey: ['sales-prospects', effectiveAgentId, viewAll],
    queryFn: async () => {
      let query = supabase.from('sales_prospects').select('*').order('created_at', { ascending: false });
      if (!viewAll && effectiveAgentId) query = query.eq('sales_agent_id', effectiveAgentId);
      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    },
  });

  const createProspect = useMutation({
    mutationFn: async () => {
      if (!user) return;
      const { error } = await supabase.from('sales_prospects').insert({
        sales_agent_id: user.id,
        prospect_name: form.prospect_name,
        prospect_type: form.prospect_type as any,
        contact_email: form.contact_email || null,
        contact_phone: form.contact_phone || null,
        notes: form.notes || null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sales-prospects'] });
      setShowCreate(false);
      setForm({ prospect_name: '', prospect_type: 'agent', contact_email: '', contact_phone: '', notes: '' });
      toast.success('Prospect added');
    },
    onError: () => toast.error('Failed to add prospect'),
  });

  const updateStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const { error } = await supabase.from('sales_prospects').update({ status: status as any }).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sales-prospects'] }),
  });

  if (isLoading) return <div className="text-center py-8 text-muted-foreground">Loading prospects...</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Prospects Pipeline</h3>
        {!viewAll && (
          <Dialog open={showCreate} onOpenChange={setShowCreate}>
            <DialogTrigger asChild>
              <Button size="sm"><Plus className="h-4 w-4 mr-1" /> Add Prospect</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>New Prospect</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <Input placeholder="Prospect name" value={form.prospect_name} onChange={(e) => setForm({ ...form, prospect_name: e.target.value })} />
                <Select value={form.prospect_type} onValueChange={(v) => setForm({ ...form, prospect_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="agent">Agent</SelectItem>
                    <SelectItem value="agency">Agency</SelectItem>
                    <SelectItem value="developer">Developer</SelectItem>
                  </SelectContent>
                </Select>
                <Input placeholder="Email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} />
                <Input placeholder="Phone" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} />
                <Textarea placeholder="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                <Button onClick={() => createProspect.mutate()} disabled={!form.prospect_name || createProspect.isPending} className="w-full">
                  {createProspect.isPending ? 'Adding...' : 'Add Prospect'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Pipeline columns */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
        {statusSteps.map((status) => {
          const filtered = prospects.filter((p: any) => p.status === status);
          const Icon = typeIcons[status] || User;
          return (
            <div key={status} className="space-y-2">
              <div className="flex items-center gap-2 mb-2">
                <Badge className={statusColors[status]}>{status.replace('_', ' ')}</Badge>
                <span className="text-xs text-muted-foreground">({filtered.length})</span>
              </div>
              {filtered.map((p: any) => {
                const PIcon = typeIcons[p.prospect_type] || User;
                return (
                  <Card key={p.id} className="shadow-sm">
                    <CardContent className="p-3 space-y-2">
                      <div className="flex items-center gap-2">
                        <PIcon className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="text-sm font-medium truncate">{p.prospect_name}</span>
                      </div>
                      <Badge variant="outline" className="text-[10px]">{p.prospect_type}</Badge>
                      {p.contact_phone && <div className="flex items-center gap-1 text-xs text-muted-foreground"><Phone className="h-3 w-3" />{p.contact_phone}</div>}
                      {p.contact_email && <div className="flex items-center gap-1 text-xs text-muted-foreground"><Mail className="h-3 w-3" />{p.contact_email}</div>}
                      {!viewAll && (
                        <Select value={p.status} onValueChange={(v) => updateStatus.mutate({ id: p.id, status: v })}>
                          <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {statusSteps.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
              {filtered.length === 0 && <p className="text-xs text-muted-foreground text-center py-4">No prospects</p>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
